import React, { useState, useEffect } from 'react';
import { X, User, FileText, Clock, AlertTriangle, CheckCircle } from 'lucide-react';
import Button from '../../../components/ui/Button';
import renewalRequestService from '../../../services/renewalRequestService';

const RenewalRequestDetailsModal = ({ request, onClose, onUpdate }) => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Submission form state
  const [submissionData, setSubmissionData] = useState({
    referenceNumber: '',
    notes: ''
  });

  useEffect(() => {
    loadNotifications();
  }, [request?.id]);

  const loadNotifications = async () => {
    try {
      setLoading(true);
      const notificationsData = await renewalRequestService?.getRenewalNotifications(request?.id);
      setNotifications(notificationsData || []);
    } catch (err) {
      console.error('Error loading notifications:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      setError('');

      if (!submissionData?.referenceNumber) {
        setError('Please enter a reference number');
        return;
      }

      await renewalRequestService?.submitRenewalRequest(request?.id, submissionData);
      
      setSuccess('Renewal request submitted successfully');
      setTimeout(() => {
        onUpdate?.();
        onClose?.();
      }, 1500);
    } catch (err) {
      console.error('Error submitting request:', err);
      setError(err?.message || 'Failed to submit renewal request');
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString)?.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { bg: 'bg-gray-100', text: 'text-gray-800', label: 'Pending' },
      notified: { bg: 'bg-blue-100', text: 'text-blue-800', label: 'Notified' },
      acknowledged: { bg: 'bg-indigo-100', text: 'text-indigo-800', label: 'Acknowledged' },
      in_progress: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'In Progress' },
      submitted: { bg: 'bg-green-100', text: 'text-green-800', label: 'Submitted' },
      approved: { bg: 'bg-green-100', text: 'text-green-800', label: 'Approved' }
    };

    const config = statusConfig?.[status] || statusConfig?.pending;

    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${config?.bg} ${config?.text}`}>
        {config?.label}
      </span>
    );
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Renewal Request Details</h2>
            <p className="text-sm text-gray-600 mt-1">{request?.documentType?.replace('_', ' ')?.toUpperCase()}</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-4 overflow-y-auto max-h-[calc(90vh-180px)]">
          {/* Success/Error Messages */}
          {success && (
            <div className="mb-4 bg-green-50 border border-green-200 rounded-lg p-4 flex items-center space-x-3">
              <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0" />
              <p className="text-green-800">{success}</p>
            </div>
          )}

          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-4 flex items-center space-x-3">
              <AlertTriangle className="h-5 w-5 text-red-600 flex-shrink-0" />
              <p className="text-red-800">{error}</p>
            </div>
          )}

          {/* Employee Information */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
              <User className="h-5 w-5 mr-2 text-blue-600" />
              Employee Information
            </h3>
            <div className="bg-gray-50 rounded-lg p-4 grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-600">Name</p>
                <p className="text-sm font-medium text-gray-900">{request?.employeeName}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Employee Number</p>
                <p className="text-sm font-medium text-gray-900">{request?.employeeNumber}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Department</p>
                <p className="text-sm font-medium text-gray-900">{request?.department}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Email</p>
                <p className="text-sm font-medium text-gray-900">{request?.employeeEmail}</p>
              </div>
            </div>
          </div>

          {/* Document Information */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
              <FileText className="h-5 w-5 mr-2 text-blue-600" />
              Document Information
            </h3>
            <div className="bg-gray-50 rounded-lg p-4 grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-600">Document Type</p>
                <p className="text-sm font-medium text-gray-900">{request?.documentType?.replace('_', ' ')?.toUpperCase()}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Document Name</p>
                <p className="text-sm font-medium text-gray-900">{request?.documentName}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Current Expiry Date</p>
                <p className="text-sm font-medium text-gray-900">{formatDate(request?.currentExpiryDate)}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Days Until Expiry</p>
                <p className={`text-sm font-bold ${
                  request?.daysUntilExpiry <= 7 ? 'text-red-600' :
                  request?.daysUntilExpiry <= 14 ? 'text-orange-600': 'text-gray-900'
                }`}>
                  {request?.daysUntilExpiry} days
                </p>
              </div>
            </div>
          </div>

          {/* Request Status */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
              <Clock className="h-5 w-5 mr-2 text-blue-600" />
              Request Status
            </h3>
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center justify-between mb-4">
                <span className="text-sm text-gray-600">Current Status</span>
                {getStatusBadge(request?.requestStatus)}
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-600">Priority</p>
                  <p className="text-sm font-medium text-gray-900 capitalize">{request?.requestPriority}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Compliance Status</p>
                  <p className="text-sm font-medium text-gray-900 capitalize">{request?.complianceStatus}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Response Deadline</p>
                  <p className="text-sm font-medium text-gray-900">{formatDate(request?.responseDeadline)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Submission Deadline</p>
                  <p className="text-sm font-medium text-gray-900">{formatDate(request?.submissionDeadline)}</p>
                </div>
              </div>

              {request?.isOverdue && (
                <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center space-x-2">
                  <AlertTriangle className="h-5 w-5 text-red-600" />
                  <span className="text-sm font-medium text-red-800">
                    Overdue by {request?.overdueDays} days
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Submission Form (if in progress or acknowledged) */}
          {(request?.requestStatus === 'acknowledged' || request?.requestStatus === 'in_progress') && (
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Submit Renewal</h3>
              <div className="bg-blue-50 rounded-lg p-4 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Reference Number *
                  </label>
                  <input
                    type="text"
                    value={submissionData?.referenceNumber}
                    onChange={(e) => setSubmissionData({ ...submissionData, referenceNumber: e?.target?.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter application reference number"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Notes
                  </label>
                  <textarea
                    value={submissionData?.notes}
                    onChange={(e) => setSubmissionData({ ...submissionData, notes: e?.target?.value })}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Add any additional notes about the submission"
                  />
                </div>
                <Button
                  onClick={handleSubmit}
                  disabled={submitting}
                  className="w-full bg-green-600 hover:bg-green-700 text-white"
                >
                  {submitting ? 'Submitting...' : 'Submit Renewal Request'}
                </Button>
              </div>
            </div>
          )}

          {/* Escalation Information */}
          {request?.escalationStatus !== 'none' && (
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                <AlertTriangle className="h-5 w-5 mr-2 text-orange-600" />
                Escalation Information
              </h3>
              <div className="bg-orange-50 rounded-lg p-4">
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div>
                    <p className="text-sm text-gray-600">Escalation Level</p>
                    <p className="text-sm font-medium text-gray-900">Level {request?.escalationLevel}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Escalation Status</p>
                    <p className="text-sm font-medium text-gray-900 capitalize">{request?.escalationStatus?.replace('_', ' ')}</p>
                  </div>
                </div>

                {request?.managerNotified && (
                  <div className="mb-3 p-3 bg-white rounded border border-orange-200">
                    <p className="text-sm font-medium text-gray-900 mb-1">Manager Notified</p>
                    <p className="text-sm text-gray-600">{formatDate(request?.managerNotifiedAt)}</p>
                    {request?.managerEmail && (
                      <p className="text-sm text-gray-600">Email: {request?.managerEmail}</p>
                    )}
                  </div>
                )}

                {request?.departmentHeadNotified && (
                  <div className="mb-3 p-3 bg-white rounded border border-orange-200">
                    <p className="text-sm font-medium text-gray-900 mb-1">Department Head Notified</p>
                    <p className="text-sm text-gray-600">{formatDate(request?.departmentHeadNotifiedAt)}</p>
                  </div>
                )}

                {request?.hrNotified && (
                  <div className="p-3 bg-white rounded border border-orange-200">
                    <p className="text-sm font-medium text-gray-900 mb-1">HR Escalated</p>
                    <p className="text-sm text-gray-600">{formatDate(request?.hrNotifiedAt)}</p>
                    {request?.hrAssignedTo && (
                      <p className="text-sm text-gray-600">Assigned to: {request?.hrAssignedTo}</p>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Notification History */}
          {notifications?.length > 0 && (
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Notification History</h3>
              <div className="space-y-3">
                {notifications?.map((notification) => (
                  <div key={notification?.id} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <p className="text-sm font-medium text-gray-900">{notification?.subject}</p>
                        <p className="text-xs text-gray-600 mt-1">
                          To: {notification?.recipientEmail} ({notification?.recipientType})
                        </p>
                      </div>
                      <span className={`text-xs px-2 py-1 rounded ${
                        notification?.notificationStatus === 'sent' ? 'bg-green-100 text-green-800' :
                        notification?.notificationStatus === 'failed'? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-800'
                      }`}>
                        {notification?.notificationStatus}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 mb-2">{notification?.message}</p>
                    <p className="text-xs text-gray-500">
                      {notification?.sentAt ? `Sent: ${formatDate(notification?.sentAt)}` : `Scheduled: ${formatDate(notification?.scheduledFor)}`}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
          <Button
            onClick={onClose}
            className="bg-gray-600 hover:bg-gray-700 text-white"
          >
            Close
          </Button>
        </div>
      </div>
    </div>
  );
};

export default RenewalRequestDetailsModal;